import { useState } from 'react';
import { AlertTriangle, Loader2, Users } from 'lucide-react';

export interface CancelClassOptions {
    reason: string;
    compensate: boolean;
    notify: boolean;
}

interface CancelClassModalProps {
    enrolledCount: number;
    isLoading?: boolean;
    onConfirm: (options: CancelClassOptions) => void;
    onClose: () => void;
}

// Confirmación para cancelar una clase COMPLETA (p.ej. instructor ausente).
// A diferencia de eliminar/ocultar el evento, esto compensa a cada inscrito con
// una recuperación, así que avisamos a cuántos clientes va a afectar.
//
// Las dos casillas son vías de escape, no el caso normal: nacen de la limpieza
// de las clases que Baby Moon tenía en festivo, donde cancelar una a una
// repartía recuperaciones que el centro no quería dar y mandaba correos sobre
// clases a las que nadie iba a ir. Por eso salen desmarcadas y con su aviso:
// quitarle una recuperación a un cliente o dejarle sin enterarse de que su
// clase no se da tiene que costar un clic consciente.
const CancelClassModal = ({ enrolledCount, isLoading, onConfirm, onClose }: CancelClassModalProps) => {
    const [reason, setReason] = useState('');
    const [skipRecovery, setSkipRecovery] = useState(false);
    const [skipEmail, setSkipEmail] = useState(false);

    const hasEnrolled = enrolledCount > 0;

    // El recuadro decía siempre "se emitirá una recuperación". Ahora dice lo que
    // de verdad va a pasar con las casillas tal y como están.
    const summary = !hasEnrolled
        ? 'No hay clientes inscritos que compensar.'
        : skipRecovery
            ? <>Los <strong>{enrolledCount}</strong> cliente(s) inscrito(s) <strong>no recibirán recuperación</strong>.</>
            : <>Se emitirá una <strong>recuperación</strong> a los <strong>{enrolledCount}</strong> cliente(s) inscrito(s).</>;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
                <div className="flex items-center mb-4">
                    <AlertTriangle size={32} className="text-yellow-500 mr-3" />
                    <h2 className="font-bold text-lg">Cancelar clase completa</h2>
                </div>

                <div className={`flex items-center gap-2 mb-4 text-sm rounded p-3 ${
                    skipRecovery && hasEnrolled ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-700'
                }`}>
                    <Users size={18} className="shrink-0" />
                    <span>{summary}</span>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo (opcional)
                </label>
                <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                    placeholder="Ej: Instructor ausente"
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
                />

                <div className="border-t border-gray-200 pt-3 mb-5 space-y-3">
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={skipRecovery}
                            onChange={(e) => setSkipRecovery(e.target.checked)}
                            disabled={!hasEnrolled}
                            className="mt-0.5 shrink-0"
                        />
                        <span className={hasEnrolled ? 'text-gray-700' : 'text-gray-400'}>
                            No dar recuperación
                            <span className="block text-xs text-gray-500">
                                Para una clase que no debería existir, como una que cayó en festivo.
                            </span>
                        </span>
                    </label>

                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={skipEmail}
                            onChange={(e) => setSkipEmail(e.target.checked)}
                            className="mt-0.5 shrink-0"
                        />
                        <span className="text-gray-700">
                            No avisar por correo
                            <span className="block text-xs text-gray-500">
                                Ni a los clientes, ni a las personas de prueba, ni a la lista de espera.
                                La cancelación sigue saliendo en la ficha de cada cliente.
                            </span>
                        </span>
                    </label>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className="py-2 px-4 bg-gray-300 rounded hover:bg-gray-400 disabled:opacity-50"
                    >
                        Volver
                    </button>
                    <button
                        onClick={() => onConfirm({
                            reason: reason.trim(),
                            compensate: !skipRecovery,
                            notify: !skipEmail,
                        })}
                        disabled={isLoading}
                        className="py-2 px-4 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 flex items-center"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 size={16} className="animate-spin mr-2" />
                                Cancelando...
                            </>
                        ) : (
                            'Cancelar clase'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CancelClassModal;
